/**
 * Intent Detection Utility
 * Detects user intent from search input for better package recommendations
 */

/**
 * Detect user intent from input text
 * @param {string} input - User's search text
 * @returns {object} Detected intent details
 */
export function detectIntent(input) {
  const lowerInput = input.toLowerCase();
  
  const intent = {
    budgetType: 'standard',
    travelType: 'general',
    urgency: 'flexible',
    groupType: 'solo',
    keywords: []
  };

  // Budget detection
  if (lowerInput.match(/cheap|budget|affordable|economical|low.?cost|save money/)) {
    intent.budgetType = 'budget';
    intent.keywords.push('budget-conscious');
  } else if (lowerInput.match(/luxury|premium|expensive|5.?star|high.?end|exclusive|lavish/)) {
    intent.budgetType = 'luxury';
    intent.keywords.push('luxury-seeking');
  } else if (lowerInput.match(/mid.?range|standard|moderate|reasonable/)) {
    intent.budgetType = 'standard';
  }

  // Travel type detection
  if (lowerInput.match(/romantic|honeymoon|couple|anniversary|love|valentine/)) {
    intent.travelType = 'romantic';
    intent.keywords.push('romantic');
  } else if (lowerInput.match(/adventure|hiking|mountain|trek|outdoor|active|sport/)) {
    intent.travelType = 'adventure';
    intent.keywords.push('adventure');
  } else if (lowerInput.match(/beach|sea|coast|ocean|sun|island|tropical/)) {
    intent.travelType = 'beach';
    intent.keywords.push('beach');
  } else if (lowerInput.match(/city|urban|culture|museum|art|historic|sightseeing/)) {
    intent.travelType = 'city';
    intent.keywords.push('culture');
  } else if (lowerInput.match(/relax|spa|wellness|calm|peaceful|quiet/)) {
    intent.travelType = 'relaxation';
    intent.keywords.push('relaxation');
  } else if (lowerInput.match(/business|work|conference|meeting/)) {
    intent.travelType = 'business';
    intent.keywords.push('business');
  }

  // Urgency detection
  if (lowerInput.match(/urgent|asap|soon|quick|immediate|emergency|last.?minute/)) {
    intent.urgency = 'urgent';
    intent.keywords.push('urgent');
  } else if (lowerInput.match(/weekend|short|2.?days|3.?days/)) {
    intent.urgency = 'short';
    intent.keywords.push('short-trip');
  } else if (lowerInput.match(/flexible|anytime|whenever|no rush/)) {
    intent.urgency = 'flexible';
  }

  // Group type detection
  if (lowerInput.match(/family|kids|children|parents/)) {
    intent.groupType = 'family';
    intent.keywords.push('family-friendly');
  } else if (lowerInput.match(/group|friends|party|together|us|we/)) {
    intent.groupType = 'group';
    intent.keywords.push('group');
  } else if (lowerInput.match(/couple|two|partner|spouse/)) {
    intent.groupType = 'couple';
    intent.keywords.push('couple');
  } else if (lowerInput.match(/solo|alone|myself|backpack/)) {
    intent.groupType = 'solo';
    intent.keywords.push('solo');
  }

  // Season preference
  if (lowerInput.match(/summer|hot|warm/)) {
    intent.season = 'summer';
  } else if (lowerInput.match(/winter|cold|snow|ski/)) {
    intent.season = 'winter';
  } else if (lowerInput.match(/spring|autumn|fall/)) {
    intent.season = 'spring-fall';
  }

  // Duration extraction
  const durationMatch = lowerInput.match(/(\d+)\s*(day|night|week)/);
  if (durationMatch) {
    const number = parseInt(durationMatch[1]);
    const unit = durationMatch[2];
    
    if (unit.startsWith('week')) {
      intent.duration = number * 7;
    } else {
      intent.duration = number;
    }
  }

  // Budget extraction (€ or $)
  const budgetMatch = lowerInput.match(/[€$]\s*(\d+)/);
  if (budgetMatch) {
    intent.maxBudget = parseInt(budgetMatch[1]);
  }

  return intent;
}

/**
 * Generate search suggestions based on intent
 * @param {object} intent - Detected intent
 * @returns {array} Suggested search refinements
 */
export function generateIntentSuggestions(intent) {
  const suggestions = [];

  if (intent.budgetType === 'budget') {
    suggestions.push('Find budget hotels', 'Show cheap flights', 'Best value deals');
  } else if (intent.budgetType === 'luxury') {
    suggestions.push('5-star hotels', 'Premium experiences', 'Luxury packages');
  }

  if (intent.travelType === 'romantic') {
    suggestions.push('Romantic getaways', 'Honeymoon packages', 'Couple-friendly hotels');
  } else if (intent.travelType === 'adventure') {
    suggestions.push('Adventure tours', 'Outdoor activities', 'Adventure hotels');
  } else if (intent.travelType === 'beach') {
    suggestions.push('Beach resorts', 'Seaside hotels', 'Coastal destinations');
  }

  if (intent.groupType === 'family') {
    suggestions.push('Family-friendly hotels', 'Kid-friendly activities', 'Family packages');
  }

  return suggestions.slice(0, 3);
}

/**
 * Format intent for AI context
 * @param {object} intent - Detected intent
 * @returns {string} Formatted string for AI prompt
 */
export function formatIntentForAI(intent) {
  let context = `User preferences: `;
  
  if (intent.budgetType) context += `${intent.budgetType} budget, `;
  if (intent.travelType) context += `${intent.travelType} travel, `;
  if (intent.groupType) context += `${intent.groupType} traveler`;
  if (intent.duration) context += `, ${intent.duration} days`;
  if (intent.maxBudget) context += `, max €${intent.maxBudget}`;

  return context;
}
